import React from 'react';
import PropTypes from 'prop-types';
import { backOff } from 'exponential-backoff';

export default function useWS({ wsUrl, canSubscribe }) {
  const ws = React.useRef(null);
  const [error, setError] = React.useState(false);
  const [inited, setInited] = React.useState(false);

  const onError = err => {
    console.error(`WebSocket error for: ${wsUrl}, Current error status: ${error}`);
    console.error(err);
    if (err instanceof Error) {
      console.error(err);
    }
    if (!error) {
      setError(true);
    }
  };

  const onClose = closeEvent => {
    console.warn('WebSocket is closing down.');
    console.warn(closeEvent);
    if (closeEvent.code !== 1000) {
      // 1000 is the standard code, when ws is closed through .close().
      console.warn('Reopening WS, because the close was abnormal. Close code:', closeEvent.code);
      reopenWs();
    }
  };

  const closeWs = () => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.close();
      ws.current = null;
    } else {
      console.warn(`Failed to close websocket ${wsUrl}. WS was either null or not open`);
    }
  };

  const createWs = throwErrorsOut => {
    let createdWs;

    try {
      createdWs = new WebSocket(wsUrl);
    } catch (e) {
      console.error('caught ws error');
      console.error(e);
      onError(e);
      if (throwErrorsOut) {
        throw e;
      }
    }

    createdWs.addEventListener('error', onError);
    createdWs.addEventListener('close', onClose);

    if (!inited) setInited(true);
    if (error) setError(false);

    return createdWs;
  };

  const reopenWs = (throwErrorsOut = false) => {
    console.log('reopenWS'); // eslint-disable-line
    closeWs();
    const newWs = createWs(throwErrorsOut);
    ws.current = newWs;
  };

  React.useEffect(
    () => {
      if (error) {
        backOff(
          () => {
            console.log('inside backOff'); // eslint-disable-line
            // lib requires to return a promise.
            return new Promise((resolve, reject) => {
              try {
                reopenWs(true);
                resolve();
              } catch (e) {
                console.log('rejecting'); // eslint-disable-line
                reject();
              }
            });
          },
          {
            delayFirstAttempt: true,
            numOfAttempts: 100,
            startingDelay: 500,
            maxDelay: 5 * 60 * 1000
          }
        );
      }
    },
    [error] //eslint-disable-line
  );

  React.useEffect(
    () => {
      if (!canSubscribe) return undefined;
      const createdWs = createWs();
      ws.current = createdWs;

      return () => {
        closeWs();
      };
    },
    [wsUrl, canSubscribe] //eslint-disable-line
  );

  return {
    ws: ws.current,
    error,
    reopenWs,
    inited
  };
}

useWS.propTypes = {
  wsUrl: PropTypes.string.isRequired
};
